import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getEventById } from 'src/shared/services/eventService';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { Alert, AlertTitle, Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { IconAndText } from 'src/shared/components/organisms/iconAndText';
import { Event as EventIcon, LocalOffer, LocationOn } from '@mui/icons-material';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import dayjs from 'dayjs';
import { MemberDetailLink } from 'src/modules/member/components/memberDetailLink';
import { getEventType } from 'src/shared/functions/getEventType';
import { formatDateAndTime } from 'src/shared/functions/formatDate';
import { Event } from '../models/event';
import { EventMeEventType } from '../components/eventMeEventType';

export const EventMeDetailsPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setIsLoading] = useState<boolean>(false);
  const [event, setEvent] = useState<Event>();
  const [showMore, setShowMore] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getEventById(id);
      if (isMounted()) setEvent(res.data);
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error);
    } finally {
      if (isMounted()) setIsLoading(false);
    }
  }, [id, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const signupParam = event?.useTickets ? 'buyTickets=true' : 'signup=true';
  const bookingUrl =
    event &&
    `https://forms.foreningssupport.se/Login?redirectUri=https://forms.foreningssupport.se/event/${event.guid}/${event?.custName}?${signupParam}&custname=${event?.custName}`;
  const lastApplicationPast = !!(event?.lastDateForApplication && dayjs().isAfter(dayjs(event.lastDateForApplication)));

  return (
    <>
      <GridWrapper>
        <Grid item xs={12} display='flex' flexDirection='column'>
          <MainContentHeader isLoading={loading} title={event?.name ?? ''} />
          {event && <EventMeEventType eventType={getEventType(event?.publicSignUp, event?.membersOnly)} />}
        </Grid>
        <Grid item xs={12}>
          <Paper>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress role='alert' aria-busy='true' aria-label={t('common:statusMessages.loading')} />
              </Box>
            )}
            {!loading && event && (
              <Grid item xs={12} p={3}>
                <GridWrapper>
                  <Grid item xs={12} lg={6} pr={{ sm: 0, lg: 3 }}>
                    {event?.images?.length > 0 && (
                      <Box
                        component='img'
                        sx={{ height: 'auto', width: '100%', borderRadius: 1, mb: 2 }}
                        src={`data:image/png;base64,${event.images?.[0].base64}`}
                      />
                    )}
                    <ContentSection mb={4} border={false} header={t('event:description')}>
                      {event.description && (
                        <Typography
                          variant='body1'
                          component='div'
                          sx={
                            !showMore
                              ? {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: '4',
                                  WebkitBoxOrient: 'vertical',
                                }
                              : undefined
                          }
                          dangerouslySetInnerHTML={{ __html: event.description }}
                        />
                      )}
                      {event.description && (
                        <Button variant='text' onClick={() => setShowMore(!showMore)}>
                          {showMore ? t('common:button.showLess') : t('common:button.showMore')}
                        </Button>
                      )}
                      {!event.description && (
                        <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>{t('event:noEventDescription')}</Typography>
                      )}
                    </ContentSection>
                    {(event.contactPersonEmail || event.contactPersonName || event.contactPersonPhone) && (
                      <ContentSection border={false} header={t('event:contactPerson')}>
                        <Typography variant='body1'>{event.contactPersonName}</Typography>
                        <MemberDetailLink linkPrefix='mailto:' details={[event.contactPersonEmail || '']} />
                        <MemberDetailLink linkPrefix='tel:' details={[event.contactPersonPhone || '']} />
                      </ContentSection>
                    )}
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    lg={6}
                    sx={{
                      pl: { sm: 0, lg: 3 },
                      borderLeft: { sm: 'none', lg: (thm) => `1px solid ${thm.palette.divider}` },
                    }}
                  >
                    <ContentSection mb={2} header={t('event:dateTime')}>
                      <IconAndText icon={<EventIcon />} text={formatDateAndTime(event.date)} />
                    </ContentSection>
                    <ContentSection mb={2} header={t('event:place')}>
                      {(event.address || event.city || event.postcode) && (
                        <IconAndText icon={<LocationOn />} text={`${event.address ?? ''}, ${event.postcode ?? ''} ${event.city ?? ''}`} />
                      )}
                    </ContentSection>
                    <ContentSection mb={2} header={t('event:notification')}>
                      <IconAndText
                        icon={<LocalOffer />}
                        text={event.memberFee === 0 || !event.memberFee ? t('event:free') : `${event.memberFee.toString()} kr`}
                      />
                    </ContentSection>

                    <Button
                      variant='contained'
                      disabled={event.isAttending || event.isFullyBooked || lastApplicationPast}
                      disableElevation
                      href={bookingUrl ?? ''}
                      target='_blank'
                    >
                      {event.useTickets ? t('event:buyTickets') : t('event:bookYourPlace')}
                    </Button>

                    {event.lastDateForApplication && (
                      <Typography variant='body1' py={1}>
                        {`${t('event:lastDate')} ${formatDateAndTime(event.lastDateForApplication)}`}{' '}
                      </Typography>
                    )}
                    {(!event.isFullyBooked || !lastApplicationPast) && event.isFullyBooked && (
                      <Alert severity='info'>
                        <AlertTitle>{t('event:eventFullyBooked')}</AlertTitle>
                        {t('event:noEmptySpots')}
                      </Alert>
                    )}
                    {event.isAttending && (
                      <Alert severity='success' sx={{ mb: 1 }}>
                        <AlertTitle>{t('event:registered')}</AlertTitle>
                        {t('event:registeredInfo')}
                      </Alert>
                    )}
                    {lastApplicationPast && (
                      <Alert severity='warning'>
                        <AlertTitle>{t('event:lastApplicationHasPast')}</AlertTitle>
                        {t('event:lastApplicationHasPastInfo')}
                      </Alert>
                    )}
                  </Grid>
                </GridWrapper>
              </Grid>
            )}
          </Paper>
        </Grid>
      </GridWrapper>
    </>
  );
};
